"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

type HorizontalScrollRailProps = {
  children: React.ReactNode;
  className?: string;
  scrollerClassName?: string;
  ariaLabel: string;
  /** Extra class on the edge fades (dark rails need the page background). */
  fadeFromClassName?: string;
  tone?: "light" | "dark";
};

function canScrollFurther(el: HTMLElement, direction: "left" | "right") {
  if (direction === "left") {
    return el.scrollLeft > 8;
  }
  return el.scrollLeft + el.clientWidth < el.scrollWidth - 8;
}

/**
 * Horizontal snap rail with edge fades and desktop arrow gutters so a
 * clipped last card is never the only cue that more items exist.
 */
export function HorizontalScrollRail({
  children,
  className,
  scrollerClassName,
  ariaLabel,
  fadeFromClassName,
  tone = "light",
}: HorizontalScrollRailProps) {
  const fadeClass =
    fadeFromClassName ??
    (tone === "dark" ? "from-[#0d0d0d] via-[#0d0d0d]/85" : "from-white via-white/85");
  const arrowClass =
    tone === "dark"
      ? "border-white/20 bg-[#1a1a1a] text-white hover:bg-[#242424] focus-visible:ring-white/50 disabled:text-white/40"
      : "border-black/10 bg-white text-zinc-800 hover:bg-zinc-50 focus-visible:ring-zinc-400 disabled:text-zinc-400";
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateAffordances = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth > 8;
    setHasOverflow(overflow);
    setCanScrollLeft(overflow && canScrollFurther(el, "left"));
    setCanScrollRight(overflow && canScrollFurther(el, "right"));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    updateAffordances();
    el.addEventListener("scroll", updateAffordances, { passive: true });
    el.addEventListener("scrollend", updateAffordances);
    const observer = new ResizeObserver(updateAffordances);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateAffordances);
      el.removeEventListener("scrollend", updateAffordances);
      observer.disconnect();
    };
  }, [updateAffordances, children]);

  const scrollByPage = (direction: "left" | "right") => {
    const el = scrollerRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const delta = Math.max(el.clientWidth * 0.8, 200);
    el.scrollBy({
      left: direction === "left" ? -delta : delta,
      behavior: prefersReduced ? "auto" : "smooth",
    });
    requestAnimationFrame(updateAffordances);
  };

  return (
    <div className={cn("relative", className)}>
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "hidden w-10 shrink-0 md:block",
            hasOverflow ? "md:block" : "md:hidden",
          )}
        >
          {canScrollLeft ? (
            <button
              type="button"
              aria-label="Scroll previous"
              onClick={() => scrollByPage("left")}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full border shadow-sm focus-visible:outline-none focus-visible:ring-2",
                arrowClass,
              )}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="relative min-w-0 flex-1">
          <div
            ref={scrollerRef}
            aria-label={ariaLabel}
            className={cn(
              "flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
              scrollerClassName,
            )}
          >
            {children}
          </div>
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r to-transparent transition-opacity duration-200 sm:w-16",
              fadeClass,
              canScrollLeft ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l to-transparent transition-opacity duration-200 sm:w-16",
              fadeClass,
              canScrollRight ? "opacity-100" : "opacity-0",
            )}
          />
        </div>
        <div
          className={cn(
            "hidden w-10 shrink-0 md:block",
            hasOverflow ? "md:block" : "md:hidden",
          )}
        >
          {canScrollRight ? (
            <button
              type="button"
              aria-label="Scroll next"
              onClick={() => scrollByPage("right")}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full border shadow-sm focus-visible:outline-none focus-visible:ring-2",
                arrowClass,
              )}
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
